'use client';

import { Suspense, useEffect, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import {
  finishNavigationLoading,
  startNavigationLoading,
} from '@/lib/navigation-loading';

function NavigationProgressInner() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [visible, setVisible] = useState(false);
  const [width, setWidth] = useState(0);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const routeKey = `${pathname ?? ''}?${searchParams?.toString() ?? ''}`;

  const clearTimers = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    if (tickTimer.current) clearInterval(tickTimer.current);
    hideTimer.current = null;
    tickTimer.current = null;
  };

  const start = () => {
    clearTimers();
    startNavigationLoading();
    setVisible(true);
    setWidth(14);
    tickTimer.current = setInterval(() => {
      setWidth((prev) => {
        if (prev >= 88) return prev;
        return prev + Math.max(1, (90 - prev) * 0.09);
      });
    }, 100);
  };

  const finish = () => {
    clearTimers();
    setWidth(100);
    finishNavigationLoading();
    hideTimer.current = setTimeout(() => {
      setVisible(false);
      setWidth(0);
    }, 220);
  };

  useEffect(() => {
    finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- complete whenever the URL settles
  }, [routeKey]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const anchor = (event.target as HTMLElement | null)?.closest('a');
      if (!anchor) return;
      if (anchor.target && anchor.target !== '_self') return;
      if (anchor.hasAttribute('download')) return;

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;

      try {
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) return;
        const nextKey = `${url.pathname}?${url.searchParams.toString()}`;
        const currentKey = `${window.location.pathname}?${window.location.search.replace(/^\?/, '')}`;
        if (nextKey === currentKey) return;
        start();
      } catch {
        /* ignore invalid href */
      }
    };

    document.addEventListener('click', onClick, true);

    return () => {
      document.removeEventListener('click', onClick, true);
      clearTimers();
      finishNavigationLoading();
    };
  }, []);

  if (!visible) return null;

  return (
    <div aria-hidden className="dash-nav-progress">
      <div className="dash-nav-progress-bar" style={{ width: `${width}%` }} />
    </div>
  );
}

/** Top progress bar + shared loading signal for dashboard navigations. */
export function NavigationProgress() {
  return (
    <Suspense fallback={null}>
      <NavigationProgressInner />
    </Suspense>
  );
}
